import { SyntheticEvent, useState } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { useDebouncedEffect } from "hooks/useDebouncedEffect";
import { useErrorProps } from "hooks/useErrorProps";
import { useSomethingWentWrongToast } from "hooks/useSomethingWentWrongToast";
import { Bank, useGetBanksForAutocomplete } from "modules/banks";
import { getValueFromPath } from "utils/getValueFromPath";

import type { UUID } from "utils";
import type { BanksAutocompleteProps } from "./BanksAutocomplete";

const DEBOUNCE_DELAY_IN_MS = 500;

interface UseBanksAutocompleteParams
  extends Omit<BanksAutocompleteProps, "sx"> {}

export function useBanksAutocomplete<AutocompleteValue extends string>({
  defaultValue,
  formValueName,
}: UseBanksAutocompleteParams) {
  const { control, formState } = useFormContext<FieldValues>();
  const { field } = useController({
    name: formValueName,
    control,
    defaultValue,
  });
  const [search, setSearch] = useState(field.value);
  const [selectedBank, setSelectedBank] = useState<null | Bank>(null);

  const {
    banks,
    isBanksError,
    isBanksFetching,
    triggerBanksFetching,
  } = useGetBanksForAutocomplete();
  const { error: isFieldError } = useErrorProps(
    getValueFromPath(formState.errors, formValueName)
  );

  function cutCountryIdFromSearch(str: string) {
    if (!str) {
      return str;
    }

    return str.replace(/^[A-Z]{2} - /, "");
  }

  useSomethingWentWrongToast(isBanksError);
  useDebouncedEffect(
    () => {
      triggerBanksFetching({
        search: cutCountryIdFromSearch(search),
      });
    },
    DEBOUNCE_DELAY_IN_MS,
    [search]
  );

  const getBankById = (bankId: UUID | null) =>
    banks.find(({ id }: { id: UUID }) => id === bankId) ?? null;

  const getOptionLabel = (bankId: UUID) => {
    const bank = getBankById(bankId) ?? selectedBank;

    return !bank ? "---" : `${bank.country.id} - ${bank.branchName}`;
  };

  const options = isBanksFetching ? [] : banks.map(({ id }) => id);

  const handleInputChange = (
    _: SyntheticEvent,
    newValue: AutocompleteValue
  ) => {
    if (newValue !== "") {
      setSearch(newValue);
    }
  };

  const handleChange = (_: unknown, selectedBankId: string | null) => {
    setSelectedBank(getBankById(selectedBankId));
    field.onChange(selectedBankId);
  };

  return {
    formField: field,
    isError: isBanksError || isFieldError,
    isLoading: isBanksFetching,
    options,
    getOptionLabel,
    handleChange,
    handleInputChange,
  };
}
